import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { 
  LayoutDashboard, MessageSquare, TrendingUp, User, LogOut, Menu, X, Users, Flame, AlertCircle, Trophy, Swords, Rocket, ChevronDown, Crosshair, ClipboardList, Ticket, Bot, ClipboardCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/');
  };

  const mlbItems = [
    { path: '/matchups', label: 'Matchups', icon: Swords },
    { path: '/hot-props', label: 'Hot Props', icon: Flame },
    { path: '/hr-tracker', label: 'HR Tracker', icon: Rocket },
    { path: '/hr-zone', label: 'HR Zone', icon: Crosshair },
    { path: '/players', label: 'MLB Players', icon: Users },
    { path: '/injuries', label: 'Injuries', icon: AlertCircle },
  ];

  const wnbaItems = [
    { path: '/wnba/matchups', label: 'Scores & Matchups', icon: Swords },
    { path: '/wnba/projections', label: 'Projections', icon: ClipboardList },
    { path: '/wnba/players', label: 'Players', icon: Users },
    { path: '/wnba/hot-props', label: 'Hot Props', icon: Flame },
  ];

  const nflItems = [
    { path: '/nfl/scores', label: 'Scores & Matchups', icon: Swords },
    { path: '/nfl/players', label: 'Players', icon: Users },
    { path: '/nfl/hot-props', label: 'Hot Props', icon: Flame },
    { path: '/nfl/td-zone', label: 'TouchDown Zone', icon: Crosshair },
    { path: '/nfl/injuries', label: 'Injuries', icon: AlertCircle },
  ];

  const nhlItems = [
    { path: '/nhl/matchups', label: 'Matchups', icon: Swords },
    { path: '/nhl/hot-props', label: 'Hot Props', icon: Flame },
    { path: '/nhl/goals-zone', label: 'Goals Zone', icon: Crosshair },
    { path: '/nhl/players', label: 'NHL Players', icon: Users },
  ];

  const nbaItems = [
    { path: '/nba/matchups', label: 'Scores & Matchups', icon: Swords },
    { path: '/nba/projections', label: 'Projections', icon: ClipboardList },
    { path: '/nba/players', label: 'Players', icon: Users },
    { path: '/nba/hot-props', label: 'Hot Props', icon: Flame },
  ];

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/props-picks', label: 'Free Props Picks', icon: Ticket },
    { path: '/sports-bot', label: 'Parlay Bot', icon: Bot },
    { path: '/my-bets', label: 'My Bets', icon: ClipboardCheck },
  ];

  const isMlbActive = mlbItems.some(i => location.pathname === i.path);
  const isWnbaActive = wnbaItems.some(i => location.pathname === i.path);
  const isNflActive = nflItems.some(i => location.pathname === i.path);
  const isNhlActive = nhlItems.some(i => location.pathname === i.path);
  const isNbaActive = nbaItems.some(i => location.pathname === i.path);

  const isActive = (path) => location.pathname === path;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#1e3a5f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/dashboard')}
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_04125061-709a-438d-a302-3baa0ffd2bcb/artifacts/3susxjnb_IMG_1638.png" 
              alt="Free Props" 
              className="h-10 w-10 logo-glow"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className={`flex items-center gap-2 font-semibold ${
                isActive('/dashboard')
                  ? 'text-brand-cyan bg-brand-cyan/10'
                  : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center gap-2 font-semibold ${
                    isMlbActive
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid="nav-mlb-dropdown"
                >
                  <span className="text-base leading-none">⚾</span>
                  MLB
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-[#0f1e36] border-[#1e3a5f]">
                {mlbItems.map((item) => (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`cursor-pointer font-semibold ${
                      isActive(item.path)
                        ? 'text-brand-cyan bg-brand-cyan/10'
                        : 'text-white hover:bg-[#112240] hover:text-white'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* WNBA Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center gap-2 font-semibold ${
                    isWnbaActive
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid="nav-wnba-dropdown"
                >
                  <span className="text-base leading-none">🏀</span>
                  WNBA
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-[#0f1e36] border-[#1e3a5f]">
                {wnbaItems.map((item) => (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`cursor-pointer font-semibold ${
                      isActive(item.path)
                        ? 'text-brand-cyan bg-brand-cyan/10'
                        : 'text-white hover:bg-[#112240] hover:text-white'
                    }`}
                    data-testid={`nav-wnba-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* NFL Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center gap-2 font-semibold ${
                    isNflActive
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid="nav-nfl-dropdown"
                >
                  <span className="text-base leading-none">🏈</span>
                  NFL
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-[#0f1e36] border-[#1e3a5f]">
                {nflItems.map((item) => (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`cursor-pointer font-semibold ${
                      isActive(item.path)
                        ? 'text-brand-cyan bg-brand-cyan/10'
                        : 'text-white hover:bg-[#112240] hover:text-white'
                    }`}
                    data-testid={`nav-nfl-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* NHL Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center gap-2 font-semibold ${
                    isNhlActive
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid="nav-nhl-dropdown"
                >
                  <span className="text-base leading-none">🏒</span>
                  NHL
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-[#0f1e36] border-[#1e3a5f]">
                {nhlItems.map((item) => (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`cursor-pointer font-semibold ${
                      isActive(item.path)
                        ? 'text-brand-cyan bg-brand-cyan/10'
                        : 'text-white hover:bg-[#112240] hover:text-white'
                    }`}
                    data-testid={`nav-nhl-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* NBA Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`flex items-center gap-2 font-semibold ${
                    isNbaActive
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid="nav-nba-dropdown"
                >
                  <span className="text-base leading-none">🏀</span>
                  NBA
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52 bg-[#0f1e36] border-[#1e3a5f]">
                {nbaItems.map((item) => (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`cursor-pointer font-semibold ${
                      isActive(item.path)
                        ? 'text-brand-cyan bg-brand-cyan/10'
                        : 'text-white hover:bg-[#112240] hover:text-white'
                    }`}
                    data-testid={`nav-nba-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {navItems.slice(1).map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-2 font-semibold ${
                  isActive(item.path)
                    ? 'text-brand-cyan bg-brand-cyan/10'
                    : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Button>
            ))}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 hover:bg-[#112240]"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8 border border-[#1e3a5f]">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-[#112240] text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-white font-medium hidden sm:block">
                    {user?.name?.split(' ')[0] || 'User'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56 bg-[#0f1e36] border-[#1e3a5f]"
              >
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer text-white hover:bg-[#112240] hover:text-white"
                  data-testid="menu-profile"
                >
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#1e3a5f]" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 hover:bg-red-500/10 hover:text-red-400"
                  data-testid="menu-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-white hover:bg-[#112240]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#1e3a5f] animate-slide-up max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="space-y-1">
              <Button
                variant="ghost"
                onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                className={`w-full justify-start flex items-center gap-3 font-semibold ${
                  isActive('/dashboard')
                    ? 'text-brand-cyan bg-brand-cyan/10'
                    : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                }`}
                data-testid="mobile-nav-dashboard"
              >
                <LayoutDashboard className="h-5 w-5" />
                Dashboard
              </Button>

              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                ⚾ MLB
              </div>
              {mlbItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold pl-8 ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}

              <div className="pt-1" />
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                🏀 WNBA
              </div>
              {wnbaItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold pl-8 ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-wnba-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}

              <div className="pt-1" />
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                🏈 NFL
              </div>
              {nflItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold pl-8 ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-nfl-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}

              <div className="pt-1" />
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                🏒 NHL
              </div>
              {nhlItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold pl-8 ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-nhl-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}

              <div className="pt-1" />
              <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                🏀 NBA
              </div>
              {nbaItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold pl-8 ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-nba-${item.label.toLowerCase().replace(/[ &]+/g, '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}

              <div className="pt-1" />
              {navItems.slice(1).map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full justify-start flex items-center gap-3 font-semibold ${
                    isActive(item.path)
                      ? 'text-brand-cyan bg-brand-cyan/10'
                      : 'text-muted-foreground hover:text-white hover:bg-[#112240]'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
